export default async function audioFingerprint() {
    const AudioContext = window.OfflineAudioContext || window.webkitOfflineAudioContext

    if (!AudioContext) {
        return null;
    }

    const hashFromIndex = 4500
    const hashToIndex = 5000
    const context = new AudioContext(1, hashToIndex, 44100)

    const oscillator = context.createOscillator()
    oscillator.type = 'triangle'
    oscillator.frequency.value = 10000

    function getHash(signal) {
        let hash = 0
        for (let i = 0; i < signal.length; ++i) {
            hash += Math.abs(signal[i])
        }
        return hash
    }

    function makeInnerError(name) {
        const error = new Error(name)
        error.name = name
        return error
    }

    function startRenderingAudio(context) {
        const renderTryMaxCount = 3
        const renderRetryDelay = 500
        const runningMaxAwaitTime = 500
        const runningSufficientTime = 5000
        let finalize = () => undefined
        let retries = 0

        const resultPromise = new Promise((resolve, reject) => {
            let isFinalized = false
            let renderTryCount = 0
            let startedRunningAt = 0

            context.oncomplete = (event) => resolve(event.renderedBuffer)

            const startRunningTimeout = () => {
                setTimeout(
                    () => reject(makeInnerError('timeout')),
                    Math.min(runningMaxAwaitTime, startedRunningAt + runningSufficientTime - Date.now()),
                )
            }

            const tryRender = () => {
                try {
                    context.startRendering()

                    switch (context.state) {
                        case 'running':
                            startedRunningAt = Date.now()
                            if (isFinalized) {
                                startRunningTimeout()
                            }
                            break

                        // Sometimes the audio context doesn't start after calling `startRendering` (in addition to the cases where
                        // audio context doesn't start at all). A known case is starting an audio context when the browser tab is in
                        // background on iPhone. Retries usually help in this case.
                        case 'suspended':
                            // The audio context can reject starting until the tab is in foreground. Long fingerprint duration
                            // in background isn't a problem, therefore the retry attempts don't count in background. It can lead to
                            // a situation when a fingerprint takes very long time and finishes successfully. FYI, the audio context
                            // can be suspended when `document.hidden === false` and start running after a retry.
                            if (!document.hidden) {
                                renderTryCount++
                            }
                            if (isFinalized && renderTryCount >= renderTryMaxCount) {
                                reject(makeInnerError('suspended'))
                            } else {
                                retries++
                                setTimeout(tryRender, renderRetryDelay)
                            }
                            break
                    }
                } catch (error) {
                    reject(error)
                }
            }

            tryRender()

            finalize = () => {
                if (!isFinalized) {
                    isFinalized = true
                    if (startedRunningAt > 0) {
                        startRunningTimeout()
                    }
                }
            }
        })

        return [resultPromise, finalize, () => retries];
    }

    const compressor = context.createDynamicsCompressor()
    compressor.threshold.value = -50
    compressor.knee.value = 40
    compressor.ratio.value = 12
    compressor.attack.value = 0
    compressor.release.value = 0.25

    oscillator.connect(compressor)
    compressor.connect(context.destination)
    oscillator.start(0)

    const [renderPromise, finishRendering, getRetries] = startRenderingAudio(context)

    const fingerprintPromise = renderPromise.then(
        (buffer) => {
            const signals = buffer.getChannelData(0).subarray(hashFromIndex);

            return {
                'hash': getHash(signals),
                'signals': signals,
                'retries': getRetries(),
            }
        },
        (error) => {
            if (error.name === 'timeout' || error.name === 'suspended') {
                return { retries: getRetries(), error: error.name };
            }
            throw error
        }
    )

    await finishRendering();

    fingerprintPromise.catch(() => undefined)
    return await fingerprintPromise;
}